import { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Form, FormInstance, Input } from 'antd';
import { ConfigProvider, CountryPhoneInput } from 'antd-country-phone-input';
import { debounce } from 'lodash';
import en from 'world_countries_lists/data/en/world.json';

import { ShippingOption } from 'Consts';
import { getAutoCompleteAddresses } from 'Services';
import { ECommerceSettings, ShippingOptions } from 'Types';

import './styles.scss';

interface Props {
  onChange: (update: Partial<ShippingOptions>) => void;
  onFormInit: (form: FormInstance) => void;
  shippingOption: ShippingOption;
  settings: ECommerceSettings;
}

export function ShippingDetails({ settings, shippingOption, onChange, onFormInit }: Props) {
  const [form] = Form.useForm();
  const [addressOptions, setAddressOptions] = useState<{ value: string }[]>([]);

  useEffect(() => {
    if (form) {
      onFormInit(form);
    }
  }, [form, onFormInit]);

  const onAddress = useMemo(
    () =>
      debounce(async (address: string) => {
        setAddressOptions([]);
        if (address.length < 4) {
          return;
        }

        setAddressOptions(
          (await getAutoCompleteAddresses(address)).map((autoComplete) => ({ value: autoComplete.address }))
        );
      }, 300),
    [setAddressOptions]
  );

  return (
    <ConfigProvider locale={en}>
      <Form form={form} className="shipping-details" autoComplete="off" onValuesChange={onChange}>
        <Form.Item label="First name" name="firstName" rules={[{ required: true, message: 'Missing first name' }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Last name" name="lastName" rules={[{ required: true, message: 'Missing last name' }]}>
          <Input />
        </Form.Item>

        <Form.Item
          initialValue={settings.phone}
          label="Phone number"
          name="phone"
          rules={[{ required: true, message: 'Missing phone' }]}
        >
          <CountryPhoneInput />
        </Form.Item>

        {shippingOption === ShippingOption.Home && (
          <Form.Item label="Full address" name="address" rules={[{ required: true, message: 'Missing address' }]}>
            <AutoComplete options={addressOptions} onSearch={(address) => onAddress(address)} />
          </Form.Item>
        )}
      </Form>
    </ConfigProvider>
  );
}
